import React, { useState, useCallback, useMemo, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import { fetchAllSkillsFromDatabase, getAllSkills, getSafeSkillName } from '../../../utils/skillsUtils';
import { useAuth } from '../../../hooks/useAuth';
import { convertCurrency, SUPPORTED_CURRENCIES } from '../../../utils/currencyUtils';
import jobService from '../../../utils/jobService';

const SALARY_SLIDER_CSS = `
  .salary-range-slider input[type='range'] {
    -webkit-appearance: none;
    appearance: none;
    background: transparent;
    pointer-events: none;
  }
  .salary-range-slider input[type='range']::-webkit-slider-thumb {
    -webkit-appearance: none;
    pointer-events: all;
    width: 18px;
    height: 18px;
    border-radius: 50%;
    background: var(--color-primary);
    border: 2px solid white;
    box-shadow: 0 1px 3px rgba(0,0,0,0.2);
    cursor: pointer;
  }
  .salary-range-slider input[type='range']::-moz-range-thumb {
    pointer-events: all;
    width: 18px;
    height: 18px;
    border-radius: 50%;
    background: var(--color-primary);
    border: 2px solid white;
    box-shadow: 0 1px 3px rgba(0,0,0,0.2);
    cursor: pointer;
  }
  .salary-range-slider input[type='range']::-moz-range-track {
    background: transparent;
  }
`;

// Static data moved outside component to avoid recreating on each render
// dbValue must match the actual database values (hyphen format: 'full-time', 'part-time', etc.)
const JOB_TYPES = [
  { value: 'full-time', label: 'Full Time', dbValue: 'full-time' },
  { value: 'part-time', label: 'Part Time', dbValue: 'part-time' },
  { value: 'contract', label: 'Contract', dbValue: 'contract' },
  { value: 'freelance', label: 'Freelance', dbValue: 'freelance' }
];

const EXPERIENCE_LEVELS = [
  { value: 'entry', label: 'Entry Level' },
  { value: 'intermediate', label: 'Intermediate' },
  { value: 'senior', label: 'Senior' },
  { value: 'expert', label: 'Expert' }
];

// Single source of truth for hourly→monthly conversion used for both salaryBounds
// computation and slider display, so the two always agree.
const HOURS_PER_MONTH = 160; // 8 hrs/day × 20 workdays (full-time equivalent)

const JobFilters = ({ filters, onFiltersChange, resultsCount }) => {
  const { userProfile } = useAuth();
  const userCurrency = userProfile?.preferred_currency || 'USD';
  const currencyConfig = SUPPORTED_CURRENCIES[userCurrency] || SUPPORTED_CURRENCIES['USD'];

  const [localFilters, setLocalFilters] = useState(filters);
  const [skillSearchInput, setSkillSearchInput] = useState('');
  const [skillOptions, setSkillOptions] = useState(getAllSkills());
  const [salaryBounds, setSalaryBounds] = useState({ min: 0, max: 200000 });
  const [pendingSalary, setPendingSalary] = useState({ min: null, max: null });

  // Sync local state when parent filters change externally (browser nav, external clear buttons)
  useEffect(() => {
    setLocalFilters(filters);
  }, [filters]);

  // Load dynamic skills from database on component mount
  useEffect(() => {
    const loadSkills = async () => {
      try {
        const allSkills = await fetchAllSkillsFromDatabase();
        setSkillOptions(allSkills);
      } catch (error) {
        console.error('Failed to load skills from database:', error);
        // Keep default skills if loading fails
      }
    };
    loadSkills();
  }, []);

  // Fetch min/max salary from active jobs to set dynamic slider bounds
  useEffect(() => {
    const loadSalaryBounds = async () => {
      // Only require budget_max (used for boundsMax); budget_min can be null
      const result = await jobService.getSalaryBounds();

      if (!result.success) {
        console.error('Failed to load salary bounds:', result.error);
        return;
      }

      const data = result.data;
      if (data?.length > 0) {
          // Convert hourly rates to monthly using the shared HOURS_PER_MONTH constant
          // so bounds and display always use the same factor.
          const toMonthly = (hourlyRate) => hourlyRate * HOURS_PER_MONTH;

          // boundsMax = single highest monthly pay across all listed jobs
          const highestMonthlyMax = data.reduce((highest, j) => {
            const monthly = toMonthly(j.budget_max);
            return monthly > highest ? monthly : highest;
          }, 0);

          const monthlyMins = data
            .filter(j => j.budget_min > 0)
            .map(j => toMonthly(j.budget_min));
          const lowestMonthlyMin = monthlyMins.length > 0 ? Math.min(...monthlyMins) : 0;

          if (highestMonthlyMax > 0) {
            setSalaryBounds({ min: lowestMonthlyMin, max: highestMonthlyMax });
          }
        }
    };
    loadSalaryBounds();
  }, []);

  const handleFilterChange = useCallback((key, value) => {
    const newFilters = { ...localFilters, [key]: value };
    setLocalFilters(newFilters);
    onFiltersChange(newFilters);
  }, [localFilters, onFiltersChange]);

  const handleSkillToggle = useCallback((skill) => {
    const currentSkills = localFilters.skills || [];
    const newSkills = currentSkills.includes(skill)
      ? currentSkills.filter(s => s !== skill)
      : [...currentSkills, skill];
    handleFilterChange('skills', newSkills);
  }, [localFilters.skills, handleFilterChange]);

  const clearAllFilters = useCallback(() => {
    const clearedFilters = {
      jobType: '',
      experienceLevel: '',
      skills: [],
      minSalary: '',
      maxSalary: '',
      sortBy: 'created_at',
      sortOrder: 'desc'
    };
    setLocalFilters(clearedFilters);
    onFiltersChange(clearedFilters);
    setSkillSearchInput('');
    setPendingSalary({ min: null, max: null });
  }, [onFiltersChange]);

  const hasActiveFilters = useMemo(() => {
    return Object.entries(localFilters).some(([key, value]) => {
      if (key === 'sortBy' || key === 'sortOrder') return false;
      if (Array.isArray(value)) return value.length > 0;
      return value !== '' && value !== undefined;
    });
  }, [localFilters]);

  const filteredSkillOptions = useMemo(() => {
    if (!skillSearchInput.trim()) return skillOptions;
    const searchLower = skillSearchInput.toLowerCase();
    return skillOptions.filter(skill =>
      getSafeSkillName(skill).toLowerCase().includes(searchLower)
    );
  }, [skillOptions, skillSearchInput]);

  return (
    <div className="bg-surface rounded-lg border border-border p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-lg font-semibold text-text-primary">Filters</h3>
        {hasActiveFilters && (
          <Button
            variant="ghost"
            size="sm"
            onClick={clearAllFilters}
            iconName="X"
            className="text-text-secondary hover:text-error"
          >
            Clear All
          </Button>
        )}
      </div>

      {/* Results Count - Only show when filters are active */}
      {hasActiveFilters && (
        <div className="mb-6 p-3 bg-primary-50 rounded-lg">
          <p className="text-sm text-primary-700 font-medium">
            {resultsCount} job{resultsCount !== 1 ? 's' : ''} found
          </p>
        </div>
      )}

      <div className="space-y-6">
        {/* Skills */}
        <div>
          <label className="block text-sm font-medium text-text-primary mb-3">
            Skills
          </label>

          {/* Selected Skills */}
          {localFilters.skills?.length > 0 && (
            <div className="mb-3">
              <div className="flex flex-wrap gap-2">
                {localFilters.skills.map((skill, index) => (
                  <span
                    key={index}
                    className="inline-flex items-center px-2 py-1 bg-primary-100 text-primary-700 text-xs rounded-full"
                  >
                    {getSafeSkillName(skill)}
                    <button
                      onClick={() => {
                        const newSkills = localFilters.skills.filter(s => s !== skill);
                        handleFilterChange('skills', newSkills);
                      }}
                      className="ml-1 hover:text-primary-900"
                    >
                      <Icon name="X" size={12} />
                    </button>
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Skills Search Bar */}
          <div className="mb-3">
            <Input
              type="text"
              placeholder="Search skills..."
              value={skillSearchInput}
              onChange={(e) => setSkillSearchInput(e.target.value)}
              className="w-full"
              icon="Search"
            />
          </div>

          {/* Available Skills */}
          <div>
            <p className="text-xs text-text-muted mb-2">
              Available skills{skillSearchInput && `: ${filteredSkillOptions.length} found`}:
            </p>
            <div className="flex flex-wrap gap-2 max-h-36 overflow-y-auto">
              {filteredSkillOptions.length > 0 ? (
                filteredSkillOptions.map((skill) => (
                  <button
                    key={skill}
                    onClick={() => handleSkillToggle(skill)}
                    className={`px-2 py-1 text-xs rounded-full border transition-colors ${
                      localFilters.skills?.includes(skill)
                        ? 'bg-primary-100 border-primary-300 text-primary-700'
                        : 'bg-surface border-border text-text-secondary hover:border-primary-300'
                    }`}
                  >
                    {getSafeSkillName(skill)}
                  </button>
                ))
              ) : (
                <p className="text-xs text-text-muted py-2">No skills match your search</p>
              )}
            </div>
          </div>
        </div>

        {/* Salary Range Dual Slider */}
        {(() => {
          // salaryBounds are monthly USD; convert to user's display currency
          const { min: boundsMinUSD, max: boundsMaxUSD } = salaryBounds;
          const boundsMin = Math.round(convertCurrency(boundsMinUSD, 'USD', userCurrency));
          const boundsMax = Math.round(convertCurrency(boundsMaxUSD, 'USD', userCurrency));
          const range = boundsMax - boundsMin || 1;
          // Filters store hourly USD; convert hourly→monthly→userCurrency for display
          // Uses the module-level HOURS_PER_MONTH (160) — same constant used when computing salaryBounds.
          const minVal = pendingSalary.min !== null
            ? pendingSalary.min
            : (localFilters.minSalary
              ? Math.round(convertCurrency(parseFloat(localFilters.minSalary) * HOURS_PER_MONTH, 'USD', userCurrency))
              : boundsMin);
          const maxVal = pendingSalary.max !== null
            ? pendingSalary.max
            : (localFilters.maxSalary
              ? Math.round(convertCurrency(parseFloat(localFilters.maxSalary) * HOURS_PER_MONTH, 'USD', userCurrency))
              : boundsMax);
          const leftPct = ((minVal - boundsMin) / range) * 100;
          const widthPct = ((maxVal - minVal) / range) * 100;
          const step = Math.max(100, Math.ceil((range / 50) / 100) * 100);
          const fmt = (v) => v >= 1000 ? `${currencyConfig.symbol}${(v / 1000).toFixed(0)}k` : `${currencyConfig.symbol}${v}`;
          return (
            <div>
              <style>{SALARY_SLIDER_CSS}</style>
              <div className="flex items-center justify-between mb-1">
                <label className="text-sm font-medium text-text-primary">Salary Range Monthly ({userCurrency})</label>
              </div>
              <div className="flex items-center justify-between mb-3">
                <span className="text-sm font-semibold text-primary">{fmt(minVal)}</span>
                <span className="text-xs text-text-muted">–</span>
                <span className="text-sm font-semibold text-primary">
                  {maxVal >= boundsMax ? `${fmt(boundsMax)}+` : fmt(maxVal)}
                </span>
              </div>
              <div className="salary-range-slider relative h-8 flex items-center">
                {/* Track background */}
                <div className="absolute w-full h-1.5 rounded-full bg-primary-100" />
                {/* Active fill */}
                <div
                  className="absolute h-1.5 rounded-full bg-primary"
                  style={{ left: `${leftPct}%`, width: `${Math.max(0, widthPct)}%` }}
                />
                {/* Min handle */}
                <input
                  type="range"
                  min={boundsMin}
                  max={boundsMax}
                  step={step}
                  value={minVal}
                  onChange={(e) => {
                    const val = parseInt(e.target.value);
                    if (val <= maxVal) setPendingSalary(p => ({ ...p, min: val }));
                  }}
                  onMouseUp={(e) => {
                    const val = parseInt(e.target.value);
                    if (val <= maxVal) {
                      // Convert monthly display value → hourly USD for DB query
                      const monthlyUsd = convertCurrency(val, userCurrency, 'USD');
                      const hourlyUsd = val <= boundsMin ? '' : String(Math.round((monthlyUsd / HOURS_PER_MONTH) * 100) / 100);
                      handleFilterChange('minSalary', hourlyUsd);
                    }
                    setPendingSalary(p => ({ ...p, min: null }));
                  }}
                  onTouchEnd={(e) => {
                    const val = parseInt(e.target.value);
                    if (val <= maxVal) {
                      const monthlyUsd = convertCurrency(val, userCurrency, 'USD');
                      const hourlyUsd = val <= boundsMin ? '' : String(Math.round((monthlyUsd / HOURS_PER_MONTH) * 100) / 100);
                      handleFilterChange('minSalary', hourlyUsd);
                    }
                    setPendingSalary(p => ({ ...p, min: null }));
                  }}
                  className="absolute w-full h-full top-0 left-0"
                  style={{ zIndex: minVal > boundsMax - range * 0.1 ? 5 : 3 }}
                />
                {/* Max handle */}
                <input
                  type="range"
                  min={boundsMin}
                  max={boundsMax}
                  step={step}
                  value={maxVal}
                  onChange={(e) => {
                    const val = parseInt(e.target.value);
                    if (val >= minVal) setPendingSalary(p => ({ ...p, max: val }));
                  }}
                  onMouseUp={(e) => {
                    const val = parseInt(e.target.value);
                    if (val >= minVal) {
                      // Convert monthly display value → hourly USD for DB query
                      const monthlyUsd = convertCurrency(val, userCurrency, 'USD');
                      const hourlyUsd = val >= boundsMax ? '' : String(Math.round((monthlyUsd / HOURS_PER_MONTH) * 100) / 100);
                      handleFilterChange('maxSalary', hourlyUsd);
                    }
                    setPendingSalary(p => ({ ...p, max: null }));
                  }}
                  onTouchEnd={(e) => {
                    const val = parseInt(e.target.value);
                    if (val >= minVal) {
                      const monthlyUsd = convertCurrency(val, userCurrency, 'USD');
                      const hourlyUsd = val >= boundsMax ? '' : String(Math.round((monthlyUsd / HOURS_PER_MONTH) * 100) / 100);
                      handleFilterChange('maxSalary', hourlyUsd);
                    }
                    setPendingSalary(p => ({ ...p, max: null }));
                  }}
                  className="absolute w-full h-full top-0 left-0"
                  style={{ zIndex: 4 }}
                />
              </div>
              <div className="flex justify-between mt-1">
                <span className="text-xs text-text-muted">{fmt(boundsMin)}</span>
                <span className="text-xs text-text-muted">{fmt(boundsMax)}+</span>
              </div>
            </div>
          );
        })()}

        {/* Job Type */}
        <div>
          <label className="block text-sm font-medium text-text-primary mb-3">
            Job Type
          </label>
          <div className="space-y-2">
            {JOB_TYPES.map((type) => {
              const isSelected = localFilters.jobType === type.dbValue;
              return (
                <label key={type.value} className="flex items-center">
                  <input
                    type="radio"
                    name="jobType"
                    value={type.dbValue}
                    checked={isSelected}
                    onChange={(e) => handleFilterChange('jobType', e.target.value)}
                    className="rounded border-border text-primary focus:ring-primary mr-2"
                  />
                  <span className="text-sm text-text-secondary">{type.label}</span>
                </label>
              );
            })}
            <label className="flex items-center">
              <input
                type="radio"
                name="jobType"
                value=""
                checked={localFilters.jobType === ''}
                onChange={(e) => handleFilterChange('jobType', e.target.value)}
                className="rounded border-border text-primary focus:ring-primary mr-2"
              />
              <span className="text-sm text-text-secondary">Any</span>
            </label>
          </div>
        </div>

        {/* Note: Remote Work filter has been removed as requested */}

        {/* Experience Level */}
        <div>
          <label className="block text-sm font-medium text-text-primary mb-3">
            Experience Level
          </label>
          <div className="space-y-2">
            {EXPERIENCE_LEVELS.map((level) => (
              <label key={level.value} className="flex items-center">
                <input
                  type="radio"
                  name="experienceLevel"
                  value={level.value}
                  checked={localFilters.experienceLevel === level.value}
                  onChange={(e) => handleFilterChange('experienceLevel', e.target.value)}
                  className="rounded border-border text-primary focus:ring-primary mr-2"
                />
                <span className="text-sm text-text-secondary">{level.label}</span>
              </label>
            ))}
            <label className="flex items-center">
              <input
                type="radio"
                name="experienceLevel"
                value=""
                checked={localFilters.experienceLevel === ''}
                onChange={(e) => handleFilterChange('experienceLevel', e.target.value)}
                className="rounded border-border text-primary focus:ring-primary mr-2"
              />
              <span className="text-sm text-text-secondary">Any Level</span>
            </label>
          </div>
        </div>
      </div>
    </div>
  );
};

export default JobFilters;
